import React, { useEffect, useState } from 'react';
import { MeetingStatus, useAttendeeStatus, useMeetingManager } from 'amazon-chime-sdk-component-library-react';
import { useSignalingApi } from '../../signaling-api';
import { JoinErrorType } from '../../signaling-api/signaling-api';
import { useVideoConfig } from '../video-config-provider/video-config-provider';
import { DuplexerConnection } from '../../../utils/duplexer';
const MEETING_LOAD_TIMEOUT = 1000 * 10; // 10 sec
const CLIENT_EVENT_SOFT_MUTE = 'client-soft-mute';
export const SignalManager = () => {
    useSessionErrorDetection();
    const [attendees, setAttendees] = useState([]);
    const meetingManager = useMeetingManager();
    const { duplexerConfig, instance } = useVideoConfig();
    const { receiveChatMessage, receiveDeleteChatMessage, receiveReaction, receiveParticipant, softMute, onRequestSoftMute, recorderStopped, } = useSignalingApi();
    useEffect(() => {
        const destructors = [];
        const connection = new DuplexerConnection({
            channel: duplexerConfig,
            instanceUpdater: {
                getInstance() {
                    return instance;
                },
            },
        });
        connection.on('server', (payload) => {
            const { chatMessageAdded, reactionAdded, chatMessageDeleted, recordingEnded } = payload;
            chatMessageAdded && receiveChatMessage(chatMessageAdded);
            reactionAdded && receiveReaction(reactionAdded);
            chatMessageDeleted && receiveDeleteChatMessage(chatMessageDeleted.id);
            if (recordingEnded) {
                recorderStopped(recordingEnded.recording);
            }
        });
        connection.on(CLIENT_EVENT_SOFT_MUTE, (payload) => {
            softMute(payload);
        });
        destructors.push(onRequestSoftMute(payload => {
            connection.send(CLIENT_EVENT_SOFT_MUTE, payload);
        }));
        return () => {
            destructors.forEach(destructor => destructor());
            connection.disconnect();
        };
    }, [instance]);
    useEffect(() => {
        // TODO: below messages are not implemented in duplexer as of now
        meetingManager.audioVideo.realtimeSubscribeToAttendeeIdPresence((attendeeId, present, participantId, _dropped) => {
            // skip secondary attendees, eg #content for screenshare
            if (attendeeId.includes('#')) {
                return;
            }
            receiveParticipant({
                participantId,
                present,
            });
            setAttendees(oldAttendees => {
                if (!oldAttendees.find(participant => participant.attendeeId === attendeeId)) {
                    return [...oldAttendees, { attendeeId, participantId }];
                }
                else {
                    return oldAttendees;
                }
            });
        });
    }, []);
    useEffect(() => {
        const destructors = [];
        return () => {
            destructors.forEach(destructor => destructor());
        };
    }, []);
    return (React.createElement(React.Fragment, null, attendees.map(participant => (React.createElement(AttendeeManager, { key: participant.participantId, participant: participant })))));
};
const AttendeeManager = ({ participant }) => {
    const { muted, videoEnabled, sharingContent } = useAttendeeStatus(participant.attendeeId);
    const { receiveParticipantStatus } = useSignalingApi();
    useEffect(() => {
        receiveParticipantStatus({
            participantId: participant.participantId,
            muted,
            video: videoEnabled,
            screenshare: sharingContent,
        });
    }, [muted, videoEnabled, sharingContent]);
    return null;
};
const useSessionErrorDetection = () => {
    const meetingManager = useMeetingManager();
    const { joinError } = useSignalingApi();
    useEffect(() => {
        let loadingTimeout;
        meetingManager.subscribeToMeetingStatus((status) => {
            if (!loadingTimeout) {
                if (status === MeetingStatus.Loading) {
                    loadingTimeout = setTimeout(() => joinError({ type: JoinErrorType.TIMEOUT }), MEETING_LOAD_TIMEOUT);
                }
            }
            else {
                if (status === MeetingStatus.Failed) {
                    joinError({ type: JoinErrorType.FAILED });
                    clearTimeout(loadingTimeout);
                }
                else if (status !== MeetingStatus.Loading) {
                    clearTimeout(loadingTimeout);
                }
            }
        });
    }, []);
};
//# sourceMappingURL=signal-manager.js.map